import React from 'react'
import { Document, Page, View, Text } from '@react-pdf/renderer'
import { PDFDocumentProps } from '../types'
import { styles } from '../styles'
import { PDFHeader } from '../components/PDFHeader'
import { PDFSummary } from '../components/PDFSummary'
import { PDFTable, AidStationsTable } from '../components/PDFTable'
import { PDFFooter } from '../components/PDFFooter'
import { formatTime, formatPace } from '../../calculations'
import { formatElevation } from '../utils'
import { calculateSegmentTimeline } from '../../timeline'

export function FullPlanDocument({ run }: PDFDocumentProps) {
  // Ensure segments are properly ordered by order_index
  const segments = (run.segments || []).sort((a, b) => (a.order_index || 0) - (b.order_index || 0))
  const aidStations = (run.aid_stations || []).sort((a, b) => a.at_distance_km - b.at_distance_km)
  const planItems = run.plan_items || []

  // Build segment table data using centralized timeline
  const timeline = calculateSegmentTimeline(segments, aidStations, undefined)

  const headers = [
    'Segment', 'Dist (km)', 'D+ (m)', 'D- (m)',
    'Pace (min/km)', 'Time', 'Cum Dist', 'ETA'
  ]

  const colWidths = ['26%', '10%', '10%', '10%', '13%', '15%', '8%', '8%']

  const segmentRows = timeline.segments.map((segmentData, index) => {
    const segment = segmentData.segment
    const elevation = formatElevation(segment.elev_gain_m || undefined, segment.elev_loss_m || undefined)

    // Format time display with stop time
    const timeDisplay = segmentData.stopTime > 0
      ? `${formatTime(segmentData.travelTime)} (+${segmentData.stopTime}m)`
      : formatTime(segmentData.totalTime)

    return [
      segment.label || `Segment ${index + 1}`,
      segment.distance_km.toFixed(2),
      elevation.gain,
      elevation.loss,
      formatPace(segmentData.pace),
      timeDisplay,
      segmentData.cumulativeDistance.toFixed(2),
      `T+${formatTime(segmentData.departureTime)}`
    ]
  })

  return (
    <Document title={`${run.title} - Full Plan`}>
      <Page size="A4" style={styles.page}>
        <PDFHeader title={run.title} date={run.date || undefined} />
        <PDFSummary run={run} />
        
        <View style={styles.marginBottomLarge}>
          <Text style={styles.sectionTitle}>Course Breakdown</Text>
          <PDFTable 
            headers={headers}
            rows={segmentRows}
            columnWidths={colWidths}
          />
        </View>

        {aidStations.length > 0 && (
          <View style={styles.marginBottomLarge}>
            <Text style={styles.sectionTitle}>Aid Stations & Crew Instructions</Text>
            <AidStationsTable 
              aidStations={aidStations}
              planItems={planItems}
            />
          </View>
        )}

        <PDFFooter />
      </Page>
    </Document>
  )
}